// AI服务接口 - 支持多个AI提供商
import { taoApi } from './taoApi';
import { backendService } from './backendService';
import { fetchTemplates } from '../data/mockData'; // 模拟数据获取
import {
  StyleTemplateRequest,
  StyleTemplateListResponse,
  UploadResponse,
  ConvertRequest,
  ConvertResponse
} from '../types/template';

class AIService {
  private mockMode = true; // 开启模拟模式
  private aiProvider: string = 'unicom'; // 默认AI提供商

  // 设置AI提供商
  setAiProvider(provider: string) {
    this.aiProvider = provider;
    console.log(`AI服务提供商已设置为: ${provider}`);
  }

  // 获取当前AI提供商
  getAiProvider(): string {
    return this.aiProvider;
  }

  // 模拟延迟
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // 生成模拟的图片URL
  private generateMockImageUrl(templateId: string): string {
    // 使用一些公开的AI生成图片服务作为模拟结果
    const mockImages = [
      'https://picsum.photos/800/1066?random=1',
      'https://picsum.photos/800/1066?random=2', 
      'https://picsum.photos/800/1066?random=3',
      'https://picsum.photos/800/1066?random=4',
      'https://picsum.photos/800/1066?random=5'
    ];
    
    // 根据模板ID选择一个图片
    const index = parseInt(templateId.slice(-1)) || 0;
    return mockImages[index % mockImages.length];
  }

  // 获取风格模板列表
  async getStyleTemplates(criteria: StyleTemplateRequest): Promise<StyleTemplateListResponse> {
    try {
      if (this.aiProvider === 'tao-local' || this.aiProvider === 'tao-remote') {
        // 使用TAO API
        const taoStyles = await taoApi.getStyleList();
        const items = taoStyles.map(style => ({
            id: style.styleId.toString(),
            name: style.styleName,
            imageUrl: style.imgUrl
          }));

        return {
          items,
          hasMore: false // TAO API 一次性返回所有模板
        };
      }

      if (this.aiProvider === 'unicom') {
        // 使用后端服务的 getStyleList 方法
        const result = await backendService.getStyleList({
          pagination: {
            page: criteria.page || 1,
            pageSize: criteria.pageSize || 20
          }
        });
        
        return {
          items: result.items,
          hasMore: result.pagination.page < result.pagination.pageCount
        };
      }

      // 模拟风格模板获取
      console.log('模拟获取风格模板列表...');
      return fetchTemplates(criteria.categoryId, criteria.page, criteria.pageSize);
    } catch (error) {
      console.error('获取风格模板失败:', error);
      throw new Error(`获取风格模板失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
  }

  // 上传图片到AI服务器
  async uploadImage(imageData: string): Promise<UploadResponse> {
    try {
      if (this.aiProvider === 'tao-local' || this.aiProvider === 'tao-remote') {
        // 使用TAO API
        const taoResult = await taoApi.uploadImage(imageData);
        return {
          success: taoResult.status === 'success',
          data: {
            imageUrl: taoResult.publicPath,
            imageId: taoResult.serverPath
          }
        };
      }

      if (this.aiProvider === 'unicom') {        
        const uploadResult = await backendService.uploadImageToYunchuang(imageData);

        return {
          success: uploadResult.success,
          data: {
            imageUrl: uploadResult.url,
            imageId: uploadResult.data?.internalUrl || uploadResult.data?.url
          }
        };
      }
      
      // 模拟上传过程
      console.log('模拟上传图片到AI服务器...');
      await this.delay(3000);

      const mockImageId = `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const mockImageUrl = 'https://picsum.photos/200/200?random=1';
      
      console.log('模拟上传成功:', { imageId: mockImageId, imageUrl: mockImageUrl });
      
      return {
        success: true,
        data: {
          imageUrl: mockImageUrl,
          imageId: mockImageId
        }
      };
    } catch (error) {
      console.error('图片上传失败:', error);
      throw new Error(`图片上传失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
  }
  
  // AI图片风格化转换
  async convertPhoto(request: ConvertRequest): Promise<ConvertResponse> {
    try {
      if (this.aiProvider === 'tao-local' || this.aiProvider === 'tao-remote') {
        // 使用TAO API
        const taoResult = await taoApi.generateStylizedImage({
          sourceImage: request.imageId,
          styleId: parseInt(request.templateId)
        });
        
        return {
          success: taoResult.status === 'success',
          data: {
            convertedImageUrl: taoResult.filePath,
            taskId: `tao_${Date.now()}`,
            status: 'completed'
          }
        };
      }

      if (this.aiProvider === 'unicom') {
        // 使用后端服务创建风格化任务
          const taskResult = await backendService.stylizeWorkflowCreateTask({
          sourceImage: request.imageId,
          styleId: request.templateId
        });
        
        if (taskResult.success && taskResult.data?.taskId) {
          return {
            success: true,
            data: {
              convertedImageUrl: '', // 任务创建时还没有结果图片
              taskId: taskResult.data.taskId,
              status: 'processing' // 返回处理中状态
            }
          };
        } else {
          throw new Error('创建风格化任务失败');
        }
      }

      // 模拟AI转换过程
      console.log('模拟AI风格化转换...');
      await this.delay(10000); // 模拟10秒转换时间

      const mockTaskId = `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const mockConvertedUrl = this.generateMockImageUrl(request.templateId);
      
      console.log('模拟转换成功:', { 
        taskId: mockTaskId, 
        convertedImageUrl: mockConvertedUrl,
        templateId: request.templateId 
      });
      
      return {
        success: true,
        data: {
          convertedImageUrl: mockConvertedUrl,
          taskId: mockTaskId,
          status: 'completed'
        }
      };
    } catch (error: any) {
      throw new Error(`图片转换失败: ${error?.message}`);
    }
  }
  
  // 轮询转换状态（如果需要的话）
  async pollConvertStatus(taskId: string): Promise<ConvertResponse> {
    try {
      if (this.aiProvider === 'unicom') {
        // 使用后端服务查询任务状态
          const statusResult = await backendService.stylizeWorkflowGetTaskStatus(taskId);
        
        if (statusResult.success && statusResult.data) {
          const { state, errorMessage, imageUrl } = statusResult.data;
          
          // state: 0-等待执行；1-执行中；2-执行失败；3-执行成功
          switch (state) {
            case 0:
            case 1:
              return {
                success: true,
                data: {
                  convertedImageUrl: '',
                  taskId: taskId,
                  status: 'processing' // 等待执行或执行中都返回processing
                },
                message: errorMessage
              };
            case 2:
              if (errorMessage === '任务已停止，但未接收到回调') {
                // 特殊处理这种情况，继续返回processing状态
                return {
                  success: true,
                  data: {
                    convertedImageUrl: '',
                    taskId: taskId,
                    status: 'processing'
                  },
                  message: errorMessage
                };
              }
              return {
                success: false,
                data: {
                  convertedImageUrl: '',
                  taskId: taskId,
                  status: 'failed' // 执行失败
                },
                message: errorMessage || '任务执行失败'
              };
            case 3:
              return {
                success: true,
                data: {
                  convertedImageUrl: this.fullImageUrl(imageUrl),
                  taskId: taskId,
                  status: 'completed' // 执行成功
                }
              };
            default:
              throw new Error(`未知的任务状态: ${state}`);
          }
        } else {
          throw new Error('获取任务状态失败');
        }
      }
      
      // 模拟状态查询
      console.log('模拟查询转换状态:', taskId);
      await this.delay(500); // 模拟查询延迟
      
      // 模拟已完成状态
      return Promise.resolve({
        success: true,
        data: {
          convertedImageUrl: 'https://picsum.photos/800/1066?random=6',
          taskId: taskId,
          status: 'completed'
        }
      });
    } catch (error) {
      console.error('状态查询失败:', error);
      throw new Error(`状态查询失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
  }

  // 设置模拟模式
  setMockMode(enabled: boolean) {
    this.mockMode = enabled;
    console.log(`AI服务${enabled ? '开启' : '关闭'}模拟模式`);
  }

  // 获取当前模式
  isMockMode(): boolean {
    return this.mockMode;
  }

  fullImageUrl(imageUrl: string|undefined): string {
    if (imageUrl) {
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl;
      } else {
        return `${backendService.backendUrl}${imageUrl}`;
      }
    }
    return imageUrl || '';
  }
}

export const aiService = new AIService();
